<template>
  <div class="ccpa-wrapper">
    <div class="opal-dropdown-container">
      <div class="opal-dropdown-toggle" @click="toggleForm">
        <b>California Consumer Privacy Act (CCPA)</b>
        <font-awesome-icon class="toggle" :icon="['fas', toggleIcon]" />
      </div>
      <div v-show="showCCPA" class="opal-dropdown-content">
        <div><b>California Residents</b></div>

        <p>
          If applicable California law provides additional protections to California residents (as
          that term is defined under applicable the law). All information collected on such
          residents, if any, will be in accordance with such laws.
          <a target="_blank" :href="ccpaCdnUrl"><u>View our California Privacy Notice</u></a> to
          learn more. California residents may also make a request to know or delete information
          below if desired.
        </p>

        <div><b>Notice at Collection</b></div>
        <p>
          Under the California Consumer Privacy Act (“CCPA”), California consumers may be entitled
          to certain notices and disclosures regarding the collection and use of their Personal
          Information. This page is intended to provide the Notice at Collection required under the
          CCPA.
        </p>
        <ul>
          <li>
            We may collect personal information from you and use it for specified purposes. For a
            list of categories of personal information that we may collect and the purposes for
            which we use such persona information, see our
            <a target="_blank" :href="ccpaCdnUrl"><u>California Privacy Notice</u>.</a>
          </li>
          <li>
            We do not sell your information for monetary value and do not disclose personal
            information to third parties in a manner that would be considered a sale of personal
            information under the CCPA.
          </li>
        </ul>
        <div class="form-wrapper">
          <div class="opal-checkboxes-container">
            <label
              class="opal-check checkbox"
              v-for="privacyNotice in privacyNotices"
              :key="privacyNotice.privacyId"
              >{{ privacyNotice.name
              }}<input
                type="checkbox"
                :checked="privacyNotice.isChecked === true"
                v-model="privacyNotice.isChecked" /><span></span
            ></label>
          </div>

          <div class="opal-button-wrapper">
            <button ref="opalButton" class="opal-button" :disabled="submitting" @click="submit">
              <span class="opal-button-text">Submit</span>
            </button>
          </div>
        </div>
        <div class="error-wrapper">
          <p class="error" v-show="submissionError">
            We were unable to complete your request. Please submit your request via email to
            <a class="inline-link error" :href="`mailto:${emailAddress}`"
              ><u>{{ emailAddress }}</u
              >.</a
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { $api } from "@/services/api";
import { useStore } from "vuex";
import { PrivacyNotice } from "@/models/disclosures";
import { EmailAddressConstants } from "@/config/constants";

const store = useStore();

const opalButton = ref(null);
const showCCPA = ref<boolean>(false);
const submissionError = ref<boolean>(false);
const privacyNotices = ref<Array<PrivacyNotice>>([]);
const californiaPrivacyNoticePdfUrl = ref<string>(
  "https://stnonprodcdn.blob.core.windows.net/public/static/privacy-policy/CCPA-Privacy-Notice-10.23.2024.pdf",
);

const toggleIcon = computed(() => (showCCPA.value ? "angle-up" : "angle-down"));
const emailAddress = computed<string>(() => EmailAddressConstants.Privacy);

const ccpaCdnUrl = computed(
  () => store.state.configuration.californiaPrivacyNotice || californiaPrivacyNoticePdfUrl.value,
);

const applicationId = computed(() => store?.state?.appData?.application?.id);
const applicantId = computed(() => store?.state?.appData?.currentApplicant?.id);
const submitting = ref<boolean>(false);

function resetCheckboxes() {
  privacyNotices.value.forEach((p: PrivacyNotice) => (p.isChecked = false));
}

function toggleForm() {
  resetCheckboxes();

  submissionError.value = false;

  showCCPA.value = !showCCPA.value;
}

function resetCCPADropdown() {
  showCCPA.value = false;

  resetCheckboxes();
}

async function submit() {
  submitting.value = true;
  submissionError.value = false;

  const response = await $api.applicant.sendApplicantPrivacyNoticeRequest(
    applicantId.value,
    applicationId.value,
    privacyNotices.value,
  );

  if (response.success) {
    resetCCPADropdown();
  } else {
    submissionError.value = true;
  }

  submitting.value = false;
}

async function getPrivacyNotices() {
  const response = await $api.consents.getPrivacyNoticeDetails(applicationId.value);

  if (response?.success) {
    privacyNotices.value = response.results;
  }
}

onMounted(async () => {
  await getPrivacyNotices();
});
</script>
<style scoped>
.opal-dropdown-toggle {
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 75%;
  margin: auto;
  padding: 8px 16px;
  height: 48px;
  cursor: pointer;
}

.opal-dropdown-container {
  text-align: center;
  position: relative;
  height: 100%;
  max-width: 1024px;
  margin: 0 auto;
}

.opal-dropdown-content {
  background-color: white;
  padding: 0 16px;
  margin: auto;
  width: 75%;
  text-align: left;
}

.form-wrapper {
  display: flex;
}

.error-wrapper,
.error-wrapper .error,
.error-wrapper a {
  animation: 0.6s ease-in;
  font-weight: var(--font-weight-bold);
  text-align: center;
  color: var(--sc-red);
}

.opal-button-wrapper {
  width: 50%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  margin-bottom: 1rem;
}

/* Checkboxes container */
.opal-checkboxes-container {
  display: flex;
  flex-direction: column;
  padding-left: 35px;
}
/* CUSTOM CHECKBOX */
label.opal-check {
  display: block;
  position: relative;
  padding-left: 35px;
  margin: 4px 0;
  cursor: pointer;
  font-weight: 600;
  font-family: "Montserrat", arial, sans-serif;
  font-size: 14px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transition: all 0.2s;
}

label.opal-check input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

label.opal-check span {
  position: absolute;
  top: 0;
  left: 0;
  height: 18px;
  width: 18px;
  background-color: white;
  transition: all 0.2s;
  border: 1px solid #000000;
}

label.opal-check:hover input ~ span {
  background-color: #ccc;
}

label.opal-check input:checked ~ span {
  background-color: #000000;
  background-color: var(--theme-color);
}

label.opal-check span:after {
  content: "";
  position: absolute;
  display: none;
}

label.opal-check input:checked ~ span:after {
  display: block;
}

label.opal-check span:after {
  left: 4px;
  top: 0px;
  width: 9px;
  height: 15px;
  border: solid white;
  border-width: 0 3px 3px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

/* Submit button styling */
.opal-button {
  border-radius: 4px;
  width: 170px;
  height: 42px;
  padding: 11px 16px;
  background-color: var(--theme-color);
  color: #ffffff;
  font-weight: 700;
}
.opal-button.loading::after {
  content: "";
  position: absolute;
  width: 16px;
  height: 16px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 4px solid transparent;
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: button-loading-spinner 1s ease infinite;
}
.opal-button.loading .opal-button-text {
  visibility: hidden;
  opacity: 0;
}
.cuscd-dark .opal-button,
.cuscd-dark .opal-button.loading::after {
  color: #000100;
}

@keyframes button-loading-spinner {
  from {
    transform: rotate(0turn);
  }

  to {
    transform: rotate(1turn);
  }
}
/* tablet and mobile sizing */
@media (max-width: 992px) {
  .opal-checkboxes-container {
    gap: 1rem;
    padding-left: 0px;
    text-align: left;
  }

  .opal-dropdown-container {
    margin: 10px;
    padding-bottom: 1rem;
  }

  .opal-dropdown-toggle,
  .opal-dropdown-content {
    width: 100%;
    text-align: center;
  }
  .opal-button-wrapper {
    width: 100%;
    margin-bottom: 1.5em;
  }
  .form-wrapper {
    flex-direction: column;
    gap: 1em;
    margin-bottom: 1rem;
  }

  ul {
    list-style-type: none;
    padding: 0;
  }
  label.opal-check span {
    width: 30px;
    height: 30px;
  }
  label.opal-check span:after {
    left: 10px;
    top: 2px;
    width: 10px;
    height: 19px;
  }
  .opal-button {
    width: 100%;
  }
}
</style>
